import { useHistory } from "react-router-dom";
import { useAuth } from "contexts/auth";
import AlertComponent from "components/alert";
import { useQuery } from "@tanstack/react-query";
import api from "services/api";
import mockData from "./mock.json";
import { IPerson } from "./types";
import { GraphLifes } from "./GraphLifes";
import { useState, useEffect } from "react";
import { Grid, Skeleton, Box } from "@mui/material";
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { Day } from 'components/calendar-week-day';
import { Dayjs, dayjs } from "utils/custom-date";

export function ProductivityTabBuyer() {
  const history = useHistory();

  const { user, backToCertificadeUpload } = useAuth();
  const isBuyerWithoutCertificate = !user?.organization?.uploadedCertificate;
  const organizationCnpj = user?.organization?.cnpj;

  // Estados para o range de datas (usando Dayjs para compatibilidade com DatePicker)
  // Padrão: últimos 30 dias (data final = hoje, data inicial = 30 dias atrás)
  const [startDate, setStartDate] = useState<Dayjs | null>(dayjs().subtract(30, 'days'));
  const [endDate, setEndDate] = useState<Dayjs | null>(dayjs());

  function handleNavigateToConnectESocial() {
    backToCertificadeUpload();
    history.push('/datasource-connect');
  };

  // Manipulador de mudança de data (similar ao form-filter)
  const handleChangeDate = (date: Dayjs | null) => {
    return date;
  };

  // Effect para validar e sincronizar as datas (baseado no form-filter)
  useEffect(() => {
    if (startDate && endDate) {
      // Se a data final for anterior à inicial, ajusta a data final
      if (endDate.isBefore(startDate)) {
        setEndDate(startDate);
      }
      
      // Se a data inicial for posterior à final, ajusta a data inicial  
      if (startDate.isAfter(endDate)) {
        setStartDate(endDate);
      }
    }
  }, [startDate, endDate]);

  // Determinar as datas a serem passadas para o GraphLifes (formato YYYY-MM-DD)
  const graphStartDate = startDate ? startDate.format('YYYY-MM-DD') : undefined;
  const graphEndDate = endDate ? endDate.format('YYYY-MM-DD') : undefined;

  const { data, isFetching, isFetched, isError } = useQuery({
    queryKey: ["collaboratorsTables", "buyer", graphStartDate, graphEndDate],
    queryFn: () => api.get<{ data: IPerson[] }>(`/v2/cdp/summary-productivity/${organizationCnpj}?startDate=${graphStartDate}&endDate=${graphEndDate}`),
    select: (data) => data.data.data,
    enabled: !!graphStartDate && !!graphEndDate,
  });

  if (isFetching) {
    return (
      <Box>
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={2}>
            <Skeleton variant="rectangular" height={40} sx={{ borderRadius: 1 }} />
          </Grid>
          <Grid item xs={2}>
            <Skeleton variant="rectangular" height={40} sx={{ borderRadius: 1 }} />
          </Grid>
        </Grid>
        <Box sx={{ backgroundColor: 'white', borderRadius: 2, p: 4, boxShadow: 1 }}>
          <Skeleton variant="text" width={200} height={36} sx={{ mb: 1 }} />
          <Skeleton variant="text" width={300} height={20} sx={{ mb: 4 }} />
          <Grid container spacing={3}>
            <Grid item xs={4}>
              <Skeleton variant="rectangular" height={70} sx={{ borderRadius: 1 }} />
            </Grid>
            <Grid item xs={4}>
              <Skeleton variant="rectangular" height={70} sx={{ borderRadius: 1 }} />
            </Grid>
            <Grid item xs={4}>
              <Skeleton variant="rectangular" height={70} sx={{ borderRadius: 1 }} />
            </Grid>
          </Grid>
          <Skeleton variant="rectangular" height={300} sx={{ borderRadius: 1, mt: 6 }} />
        </Box>
      </Box>
    )
  }

  return (
    <>
      {isBuyerWithoutCertificate && (
        <AlertComponent
          severity='warning'
          title='Conecte-se ao eSocial!'
          content='A conexão é necessária para ativar as automações da plataforma. Conecte agora ou convide um responsável (caso não tenha feito). Para configurar,'
          navigateText='clique aqui.'
          onNavigate={handleNavigateToConnectESocial}
        />
      )}

      {/* Filtro de Datas - seguindo padrão do form-filter */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={2}>
          <DatePicker
            label='Data Inicial'
            format='DD/MM/YYYY'
            disableHighlightToday
            showDaysOutsideCurrentMonth
            displayWeekNumber
            maxDate={dayjs(new Date())}
            value={startDate}
            onChange={(date) => setStartDate(handleChangeDate(date))}
            slots={{ day: Day }}
            slotProps={{
              textField: {
                size: 'small',
                margin: 'none',
                fullWidth: true,
                variant: 'outlined',
              },
            }}
          />
        </Grid>

        <Grid item xs={2}>
          <DatePicker
            label='Data Final'
            format='DD/MM/YYYY'
            disableHighlightToday
            maxDate={dayjs(new Date())}
            minDate={startDate || undefined}
            showDaysOutsideCurrentMonth
            displayWeekNumber
            value={endDate}
            onChange={(date) => setEndDate(handleChangeDate(date))}
            slots={{ day: Day }}
            slotProps={{
              textField: {
                size: 'small',
                margin: 'none',
                fullWidth: true,
                variant: 'outlined',
              },
            }}
          />
        </Grid>
      </Grid>

      {data && (
        <GraphLifes
          data={data}
          startDate={graphStartDate}
          endDate={graphEndDate}
        />
      )}
    </>
  );
}
